<!-- NOTICE: KAPTION-INTEGRATION -->
<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { format, isValid, isBefore, startOfMinute, addHours } from 'date-fns';
import {
  es as esLocale,
  pt as ptLocale,
  fr as frLocale,
  de as deLocale,
  it as itLocale,
  tr as trLocale,
  enUS as enLocale,
} from 'date-fns/locale';
import * as chrono from 'chrono-node';
import { OnClickOutside } from '@vueuse/components';

const props = defineProps({
  modelValue: {
    type: [String, Date, null],
    default: null,
  },
  label: {
    type: String,
    default: '',
  },
  placeholder: {
    type: String,
    default: '',
  },
  required: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['update:modelValue']);

const { locale, t } = useI18n();

const inputValue = ref('');
const parsedDate = ref(null);
const showSuggestions = ref(false);
const isFocused = ref(false);
const errorMessage = ref('');

// Get date-fns locale based on current i18n locale
const getDateFnsLocale = () => {
  const lang = locale.value.split('-')[0];
  const locales = {
    en: enLocale,
    es: esLocale,
    pt: ptLocale,
    fr: frLocale,
    de: deLocale,
    it: itLocale,
    tr: trLocale,
  };
  return locales[lang] || enLocale;
};

// Get the appropriate chrono parser for the current locale
const getChronoParser = () => {
  const lang = locale.value.split('-')[0]; // Get base language (e.g., 'en' from 'en-US')

  // Map of supported locales in chrono
  const parsers = {
    en: chrono.en,
    es: chrono.es,
    pt: chrono.pt,
    fr: chrono.fr,
    de: chrono.de,
    nl: chrono.nl,
    ja: chrono.ja,
    ru: chrono.ru,
    uk: chrono.uk,
  };

  return parsers[lang] || chrono.en; // Default to English if language not supported
};

// Helper function to create a date for "tomorrow at X"
const getTomorrowAt = hours => {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  tomorrow.setHours(hours, 0, 0, 0);
  return tomorrow;
};

// Helper function to get next Monday at X
const getNextMondayAt = hours => {
  const today = new Date();
  const dayOfWeek = today.getDay();
  const daysUntilMonday = dayOfWeek === 0 ? 1 : 8 - dayOfWeek;
  const nextMonday = new Date();
  nextMonday.setDate(today.getDate() + daysUntilMonday);
  nextMonday.setHours(hours, 0, 0, 0);
  return nextMonday;
};

// Helper function to get next week (same day, same time, next week)
const getNextWeek = () => {
  const nextWeek = new Date();
  nextWeek.setDate(nextWeek.getDate() + 7);
  return nextWeek;
};

// Common suggestions - using computed dates instead of text values
// This ensures the suggestions work regardless of language
const commonSuggestions = computed(() => [
  {
    label: t('CAMPAIGN.DATETIME_PICKER.NOW'),
    getDate: () => new Date(),
  },
  {
    label: t('CAMPAIGN.DATETIME_PICKER.IN_1_HOUR'),
    getDate: () => addHours(new Date(), 1),
  },
  {
    label: t('CAMPAIGN.DATETIME_PICKER.IN_2_HOURS'),
    getDate: () => addHours(new Date(), 2),
  },
  {
    label: t('CAMPAIGN.DATETIME_PICKER.TOMORROW_AT_9AM'),
    getDate: () => getTomorrowAt(9),
  },
  {
    label: t('CAMPAIGN.DATETIME_PICKER.TOMORROW_AT_2PM'),
    getDate: () => getTomorrowAt(14),
  },
  {
    label: t('CAMPAIGN.DATETIME_PICKER.NEXT_MONDAY_AT_9AM'),
    getDate: () => getNextMondayAt(9),
  },
  {
    label: t('CAMPAIGN.DATETIME_PICKER.NEXT_WEEK'),
    getDate: () => getNextWeek(),
  },
]);

// Format date using locale-aware formatting
const formatDateLocalized = date => {
  const dateLocale = getDateFnsLocale();
  return format(date, 'PPPp', { locale: dateLocale });
};

// Computed placeholder that falls back to translation if not provided
const computedPlaceholder = computed(() => {
  return props.placeholder || t('CAMPAIGN.DATETIME_PICKER.PLACEHOLDER');
});

// Initialize input value from modelValue
watch(
  () => props.modelValue,
  newValue => {
    if (newValue && !inputValue.value) {
      const date = new Date(newValue);
      if (isValid(date)) {
        parsedDate.value = date;
        inputValue.value = formatDateLocalized(date);
      }
    }
  },
  { immediate: true }
);

const parseNaturalLanguage = text => {
  if (!text || text.trim() === '') {
    return null;
  }

  const trimmedText = text.trim().toLowerCase();

  // Handle "now" explicitly in multiple languages
  const nowKeywords = [
    'now',
    'ahora',
    'agora',
    'maintenant',
    'jetzt',
    'nu',
    '今',
  ];
  if (nowKeywords.includes(trimmedText)) {
    return new Date();
  }

  // Get the appropriate parser for the current locale
  const parser = getChronoParser();

  // Use chrono to parse natural language with locale support
  const results = parser.parse(trimmedText, new Date(), { forwardDate: true });

  if (results.length > 0) {
    return results[0].start.date();
  }

  return null;
};

const handleInput = () => {
  errorMessage.value = '';
  const parsed = parseNaturalLanguage(inputValue.value);

  if (parsed) {
    // Check if date is in the past
    const now = startOfMinute(new Date());
    if (isBefore(parsed, now)) {
      errorMessage.value = t('CAMPAIGN.DATETIME_PICKER.PAST_DATE_ERROR');
      parsedDate.value = null;
      emit('update:modelValue', null);
      return;
    }

    parsedDate.value = parsed;
    emit('update:modelValue', parsed.toISOString());
  } else if (inputValue.value.trim() !== '') {
    errorMessage.value = t('CAMPAIGN.DATETIME_PICKER.INVALID_DATE_ERROR');
    parsedDate.value = null;
    emit('update:modelValue', null);
  } else {
    parsedDate.value = null;
    emit('update:modelValue', null);
  }
};

const selectSuggestion = suggestion => {
  // Get the actual date from the suggestion
  const date = suggestion.getDate();

  // Check if date is in the past
  const now = startOfMinute(new Date());
  if (isBefore(date, now)) {
    errorMessage.value = t('CAMPAIGN.DATETIME_PICKER.PAST_DATE_ERROR');
    parsedDate.value = null;
    emit('update:modelValue', null);
    return;
  }

  // Set the formatted date as input value
  inputValue.value = formatDateLocalized(date);
  parsedDate.value = date;
  errorMessage.value = '';
  emit('update:modelValue', date.toISOString());

  showSuggestions.value = false;
  isFocused.value = false;
};

const handleFocus = () => {
  isFocused.value = true;
  showSuggestions.value = true;
};

const handleClickOutside = () => {
  showSuggestions.value = false;
  isFocused.value = false;
};

const formattedPreview = computed(() => {
  if (!parsedDate.value) return '';
  const dateLocale = getDateFnsLocale();
  // Use a more detailed format: "Monday, November 25, 2024 at 2:00 PM"
  return format(parsedDate.value, 'PPPP p', { locale: dateLocale });
});
</script>

<template>
  <OnClickOutside @trigger="handleClickOutside">
    <div class="flex flex-col gap-0 relative">
      <label
        v-if="label"
        class="text-sm font-medium text-n-slate-12 mb-1"
        :class="{
          'after:content-[\'*\'] after:ml-0.5 after:text-n-ruby-11': required,
        }"
      >
        {{ label }}
      </label>

      <div class="relative">
        <input
          v-model="inputValue"
          type="text"
          :placeholder="computedPlaceholder"
          class="w-full px-3 py-2 text-sm border rounded-lg transition-colors bg-n-alpha-black2 text-n-slate-12 placeholder:text-n-slate-10 focus:outline-none focus:ring-2 focus:ring-opacity-20"
          :class="[
            errorMessage
              ? 'border-n-ruby-7 focus:border-n-ruby-8 focus:ring-n-ruby-8'
              : parsedDate
                ? 'border-n-green-7 focus:border-n-green-8 focus:ring-n-green-8'
                : 'border-n-slate-6 focus:border-n-brand focus:ring-n-brand',
          ]"
          @input="handleInput"
          @focus="handleFocus"
        />

        <!-- Success/Error Icon -->
        <div
          v-if="parsedDate || errorMessage"
          class="absolute right-3 top-1/2 -translate-y-1/2"
        >
          <span
            v-if="parsedDate"
            class="i-lucide-check text-n-green-11 w-4 h-4"
          />
          <span
            v-else-if="errorMessage"
            class="i-lucide-alert-circle text-n-ruby-11 w-4 h-4"
          />
        </div>
      </div>

      <!-- Preview -->
      <div
        v-if="formattedPreview && !errorMessage"
        class="text-xs text-n-green-11 mt-1"
      >
        <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
        {{
          t('CAMPAIGN.DATETIME_PICKER.SCHEDULED_FOR', {
            date: formattedPreview,
          })
        }}
      </div>

      <!-- Error Message -->
      <div v-if="errorMessage" class="text-xs text-n-ruby-11 mt-1">
        {{ errorMessage }}
      </div>

      <!-- Suggestions Dropdown -->
      <div
        v-if="showSuggestions && isFocused"
        class="absolute top-full left-0 right-0 mt-1 bg-n-background border border-n-slate-6 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto"
      >
        <div class="p-2">
          <div class="text-xs font-medium text-n-slate-11 px-2 py-1">
            <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
            {{ t('CAMPAIGN.DATETIME_PICKER.COMMON_OPTIONS') }}
          </div>
          <button
            v-for="(suggestion, index) in commonSuggestions"
            :key="index"
            type="button"
            class="w-full text-left px-2 py-2 text-sm rounded hover:bg-n-alpha-black2 transition-colors text-n-slate-12"
            @click="selectSuggestion(suggestion)"
          >
            {{ suggestion.label }}
          </button>
        </div>
      </div>
    </div>
  </OnClickOutside>
</template>
